import React, { useState, useRef, useEffect } from 'react';
import { Tag as TagIcon, Plus, X } from 'lucide-react';

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface TagEditorProps {
  recipeId: string;
  currentTags: string[]; // tag names
  availableTags: Tag[];
  onAddTag: (recipeId: string, tagId: string) => Promise<void>;
  onRemoveTag: (recipeId: string, tagId: string) => Promise<void>;
  disabled?: boolean;
}

export const TagEditor: React.FC<TagEditorProps> = ({
  recipeId,
  currentTags,
  availableTags,
  onAddTag,
  onRemoveTag,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleTag = async (tag: Tag) => {
    if (isUpdating || disabled) return;

    setIsUpdating(true);
    try {
      const isCurrentlyTagged = currentTags.includes(tag.name);
      
      if (isCurrentlyTagged) {
        await onRemoveTag(recipeId, tag.id);
      } else {
        await onAddTag(recipeId, tag.id);
      }
    } catch (error) {
      console.error('Error toggling tag:', error);
      alert('Erreur lors de la modification du tag');
    } finally {
      setIsUpdating(false);
    }
  };

  const currentTagObjects = availableTags.filter(tag => currentTags.includes(tag.name));

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Display current tags + add button */}
      <div className="flex flex-wrap gap-1 items-center">
        {currentTagObjects.length === 0 ? (
          <span className="text-sm text-gray-400">-</span>
        ) : (
          currentTagObjects.slice(0, 2).map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
              style={{
                backgroundColor: `${tag.color}20`,
                color: tag.color,
                border: `1px solid ${tag.color}40`
              }}
            >
              {tag.name}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleTag(tag);
                }}
                disabled={isUpdating || disabled}
                className="hover:bg-black/10 rounded-full p-0.5 disabled:opacity-50"
                title="Retirer ce tag"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))
        )}
        
        {currentTagObjects.length > 2 && (
          <span className="inline-flex items-center px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded">
            +{currentTagObjects.length - 2}
          </span>
        )}
        
        {/* Add tag button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Ajouter un tag"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase">
            Sélectionner des tags
          </div>
          <div className="border-t border-gray-100"></div>
          
          {availableTags.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">
              Aucun tag disponible
            </div>
          ) : (
            availableTags.map((tag) => {
              const isSelected = currentTags.includes(tag.name);
              
              return (
                <button
                  key={tag.id}
                  onClick={() => handleToggleTag(tag)}
                  disabled={isUpdating}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-sm text-gray-700">{tag.name}</span>
                  </div>
                  {isSelected && (
                    <TagIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

